/**
 * `useConsoleActions` 家族的共用形狀：對外型別、常數與預設值。
 *
 * 拆檔（2026-10-27）後放在這裡，`useConsoleActions.ts` 再原樣 re-export，
 * 所以呼叫端的 `import type { ConsoleActions } from '../hooks/useConsoleActions'` 不必改。
 * 這裡只有型別與常數，**沒有任何邏輯**——放邏輯會讓四支 hook 互相依賴。
 */
import type { Dispatch, SetStateAction } from 'react';
import { useHighlights } from '@chat-rea/console-ui';
import type {
  Account,
  Conversation,
  DroppedSession,
  HealthAccount,
  InboxEvent,
  LoginMode,
  Notice,
  NoticeKind,
  PoolAccount,
  PoolRegisterOutcome,
  PoolServiceFilter,
  PushNoticeInput,
  RoomInfo,
  Service,
  UseInboxResult,
} from '@chat-rea/console-ui';

/** 沒選對話時的預設（後端與 docs 的預設低人流房）。 */
export const DEFAULT_SERVICE: Service = 'ek21';
export const DEFAULT_ROOM = 'vi_25';

/** 送出／進房失敗且後端說 session 已經不存在時，對使用者講的那一句（清單也會同步移除）。 */
export const INVALID_SESSION_NOTICE = '這個帳號已失效，已從清單移除';

/** 「全部離開」兩顆之間留的間隔（同「全部進房」的節奏，不對站方平行猛打）。 */
export const LEAVE_GAP_MS = 400;

export type DrawerTab = 'rooms' | 'health';

/**
 * 帳號池「註冊 n 顆」的結果 → 一句結算（純函式，拆檔時從 `useBatchActions` 抽出來）。
 * 一顆一張卡、不壓成一句話也不互相蓋掉：全成功＝success；全失敗＝error；部分＝warning。
 */
export function poolRegisterNotices(result: {
  service: Service;
  requested: number;
  created: number;
  results: PoolRegisterOutcome[];
}): PushNoticeInput[] {
  const failed = result.results.filter((item) => !item.ok);
  const detail = failed
    .map((item) => `${item.username}：${item.reason ?? '未回報原因'}`)
    .join('｜');
  if (failed.length === 0) {
    return [
      { kind: 'success', message: `已註冊 ${result.created}/${result.requested} 顆 ${result.service} 帳號進池` },
    ];
  }
  if (result.created === 0) {
    return [{ kind: 'error', message: `這批 ${result.requested} 顆都沒進池`, detail }];
  }
  return [
    {
      kind: 'warning',
      message: `只進池 ${result.created}/${result.requested} 顆（其餘逐顆見細節）`,
      detail,
    },
  ];
}

/**
 * 「全部離開」的三個狀態。形狀刻意跟 `console-ui` 的 `AccountPool` props
 * （`leaveAllProgress`／`leaveAllResult`）一致，接線時 1:1 傳進去，不必再轉一層。
 */
export interface LeaveAllProgress {
  index: number;
  total: number;
  /** 正在掛下線的暱稱（讓畫面看得出卡在哪一顆，不是只有數字在跳）。 */
  nickname: string;
}

export interface LeaveAllFailure {
  accountId: string;
  nickname: string;
  reason: string;
}

export interface LeaveAllResult {
  total: number;
  left: number;
  failed: LeaveAllFailure[];
}

/**
 * 一次「開通」要記下來的全部東西：失敗後按「重試」要原封不動再送一次
 * （同一個站、同一個暱稱、同一種登入方式、同一間房），不能叫使用者重填。
 */
export interface OpeningAttempt {
  service: Service;
  nickname: string;
  /**
   * 要進的房；`null`＝不帶 `room`，交給後端用該站預設房。
   * 跨站開通時**不可以**沿用目前身分的房號（房號只在自己那一站有效）。
   */
  room: string | null;
  loginMode?: LoginMode;
  accountUsername?: string;
  /** 重建已失效身分時，成功後要收掉的那張提示卡。 */
  droppedId?: string;
  /** 成功後要顯示的一句提示（重建／帳號池入口各自不同，先組好再帶進來）。 */
  notice?: string;
}

/** 畫面用的開通狀態：`startedAt` 給 `OpeningPanel` 自己算已耗秒數。 */
export interface OpeningState {
  attempt: OpeningAttempt;
  startedAt: number;
  status: 'running' | 'failed';
  error: string | null;
}

/**
 * 對外介面：四支 hook 的回傳值在這裡一次宣告，`useConsoleActions` 只負責組裝。
 * **欄位一個都不能少**——所有畫面都吃這一份型別，縮了介面就等於改呼叫端。
 */
export interface ConsoleActions {
  // 清單／提醒
  conversations: Conversation[];
  unreadTotal: number;
  activeId: string | null;
  active: Conversation | null;
  activeEvents: UseInboxResult['activeEvents'];
  eventsLoading: boolean;
  inboxLoading: boolean;
  /** 清單載入錯誤（#02）：給左欄就地顯示「連不上／請重新登入＋重試」。 */
  inboxError: string | null;
  inboxErrorKind: 'offline' | 'unauthorized' | 'error' | null;
  /** 清單是否成功載入過（false + 0 筆＝載入中，不是空）。 */
  inboxLoaded: boolean;
  /** 目前對話的事件載入錯誤（#02）：給對話串顯示「這段讀不到」。 */
  eventsError: string | null;
  eventsErrorKind: 'offline' | 'unauthorized' | 'error' | null;
  dropped: UseInboxResult['dropped'];
  /** 通知佇列（錯誤／警告／成功／提示並存，各自可關）。 */
  notices: Notice[];
  dismissNotice: (id: string) => void;
  /** 重試成功時清掉 error 這一類（由 hook 內部的成功路徑自動呼叫）。 */
  clearErrorNotices: (kind: NoticeKind) => void;
  /** 還有沒關掉的錯誤。 */
  noticeError: boolean;
  openConversation: (id: string | null) => void;
  dismissGone: (id: string) => void;
  refresh: () => Promise<void>;
  /** 重抓目前對話的事件（#02 的「這段讀不到 → 重試」用）。 */
  refreshEvents: (id?: string) => Promise<void>;
  highlights: ReturnType<typeof useHighlights>;
  highlightKeywords: string[];
  keywordDraft: string;
  setKeywordDraft: (value: string) => void;
  submitKeyword: () => void;

  // 面板狀態
  drawerOpen: boolean;
  setDrawerOpen: Dispatch<SetStateAction<boolean>>;
  drawerTab: DrawerTab;
  setDrawerTab: (tab: DrawerTab) => void;
  mobileThread: boolean;
  setMobileThread: (open: boolean) => void;

  // 房間
  rooms: RoomInfo[];
  roomsLoading: boolean;
  roomsError: string | null;
  currentRoom: string | null;
  currentService: Service;
  targetRoom: string;
  loadRooms: (service: Service) => Promise<void>;
  joinRoom: (room: RoomInfo) => void;

  // 送出
  busy: boolean;
  say: (text: string) => Promise<void>;
  sendPm: (target: string, text: string) => Promise<void>;
  /** 重送一則送失敗的訊息（對話串上「送出失敗 · 重送」按鈕）。 */
  retryOutgoing: (event: InboxEvent) => Promise<void>;
  /** 「用第二個帳號確認」：只讀一次（≤3 次 GET），把結果講成一句人話。 */
  verifyDelivery: (event: InboxEvent) => Promise<void>;

  // 帳號
  createAccount: () => void;
  createError: string | null;
  /** 開通中／失敗的狀態（`null`＝沒有在開通，也沒有待處理的失敗）。 */
  opening: OpeningState | null;
  /** 原封不動再開一次（失敗區塊上的「重試」）。 */
  retryOpening: () => void;
  /** 收掉失敗區塊（使用者按「關閉」）。 */
  dismissOpening: () => void;
  reconnectAccount: (account: HealthAccount) => void;
  deleteAccount: (account: HealthAccount) => void;
  rebuildDropped: (session: DroppedSession) => void;

  // 帳號頁：已開的身分（session）
  /** 勾選的身分（`AccountPool` 的 checkbox）；沒勾＝全部。 */
  sessionPicked: string[];
  toggleSessionPick: (id: string) => void;
  /** 全部進房：把勾選的（沒勾＝全部）依序搬進 `room`。 */
  joinSessions: (room: string) => Promise<void>;
  /** 全部離開：逐顆 `DELETE /api/sessions/:id`，不刪帳號、不改後端。 */
  leaveAll: () => Promise<void>;
  /** 進行中的進度；不在跑就是 null（按鈕靠它顯示「第 n／N」並鎖住）。 */
  leaveAllProgress: LeaveAllProgress | null;
  /** 最近一次的結算；下一次開始時清空。 */
  leaveAllResult: LeaveAllResult | null;

  // 帳號池
  poolAccounts: PoolAccount[];
  poolLoading: boolean;
  poolError: string | null;
  poolRegistering: boolean;
  poolResults: PoolRegisterOutcome[];
  poolRegisterError: string | null;
  poolCooldownUntil: number | null;
  poolFilter: PoolServiceFilter;
  setPoolFilter: (filter: PoolServiceFilter) => void;
  poolRegisterService: Service;
  setPoolRegisterService: (service: Service) => void;
  loadPool: () => Promise<void>;
  registerPool: (count: number) => void;
  usePoolAccount: (target: PoolAccount | string) => Promise<void>;
  deletePoolAccount: (account: PoolAccount) => void;
  /** 改顯示名稱（`null` = 清除）；失敗會丟出具體訊息（重名／長度）。 */
  renameAccount: (account: Account, displayName: string | null) => Promise<void>;
}
